import React, { memo, useCallback } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import OutlinedInput from '@mui/material/OutlinedInput';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import Remove from '@mui/icons-material/Remove';
import type { SxProps, Theme } from '@mui/material/styles';
import type { HazmatInfo } from '../../types/label';
import CheckToggle from './CheckToggle';
import HazmatFields from './HazmatFields';
import { ItemDraft, LIMITED_QUANTITY, isBlank } from './labelForm';
import { inputSx, monoInputSx } from './styles';

const captionSx: SxProps<Theme> = { fontSize: 11, fontWeight: 600, color: 'text.secondary' };

const Caption: React.FC<{ text: string }> = ({ text }) => (
  <Box component="span" sx={captionSx}>
    {text}
    <Box component="span" aria-hidden sx={{ color: 'error.main' }}>
      {' *'}
    </Box>
  </Box>
);

const itemInputSx: SxProps<Theme> = [inputSx, monoInputSx, { height: 36 }] as SxProps<Theme>;

const stepperSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  height: 36,
  borderRadius: 2,
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  overflow: 'hidden',
  '& .MuiIconButton-root': { width: 32, height: '100%', borderRadius: 0, color: 'text.secondary' },
};

interface QuantityStepperProps {
  value: number;
  label: string;
  onChange: (value: number) => void;
}

const QuantityStepper: React.FC<QuantityStepperProps> = ({ value, label, onChange }) => (
  <Box sx={stepperSx}>
    <IconButton aria-label={`Decrease ${label}`} disabled={value <= 1} onClick={() => onChange(Math.max(1, value - 1))}>
      <Remove sx={{ fontSize: 16 }} />
    </IconButton>
    <InputBase
      value={value}
      onChange={(e) => onChange(Math.max(1, parseInt(e.target.value, 10) || 1))}
      inputProps={{ inputMode: 'numeric', 'aria-label': label, style: { textAlign: 'center', fontWeight: 600, fontVariantNumeric: 'tabular-nums' } }}
      sx={{ flex: 1, minWidth: 0 }}
    />
    <IconButton aria-label={`Increase ${label}`} onClick={() => onChange(value + 1)}>
      <AddIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </Box>
);

/** Item callbacks; all keyed by item key so the same stable functions serve every row. */
export interface OrderItemHandlers {
  onItemChange: (key: string, patch: Partial<ItemDraft>) => void;
  onToggleLimitedQuantity: (key: string) => void;
  onToggleHazmat: (key: string) => void;
  onHazmatChange: (key: string, patch: Partial<HazmatInfo>) => void;
  onRemoveItem: (key: string) => void;
}

interface OrderItemRowProps extends OrderItemHandlers {
  item: ItemDraft;
  /** Accessible name prefix, e.g. "Package 2 item 1". */
  name: string;
  removable: boolean;
}

const OrderItemRow: React.FC<OrderItemRowProps> = ({
  item,
  name,
  removable,
  onItemChange,
  onToggleLimitedQuantity,
  onToggleHazmat,
  onHazmatChange,
  onRemoveItem,
}) => {
  const { key } = item;
  const setQuantity = useCallback((quantity: number) => onItemChange(key, { quantity }), [key, onItemChange]);
  const changeHazmat = useCallback((patch: Partial<HazmatInfo>) => onHazmatChange(key, patch), [key, onHazmatChange]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        px: 1.5,
        py: 1.25,
        borderRadius: 2.5,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px 12px', alignItems: 'flex-end' }}>
        <Box component="label" sx={{ flex: '1 1 180px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Caption text="Product ID" />
          <OutlinedInput
            value={item.productId}
            placeholder="SKU or product ID"
            error={isBlank(item.productId)}
            onChange={(e) => onItemChange(key, { productId: e.target.value })}
            inputProps={{ 'aria-label': `${name} product ID` }}
            sx={itemInputSx}
          />
        </Box>
        {/* Not a <label>: clicking the caption would activate the stepper's first button. */}
        <Box sx={{ flex: '0 0 112px', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Caption text="Quantity" />
          <QuantityStepper value={item.quantity} label={`${name} quantity`} onChange={setQuantity} />
        </Box>
        <Box sx={{ flex: '1 1 auto', display: 'flex', gap: 0.5, flexWrap: 'wrap', pb: '2px' }}>
          <CheckToggle
            label="Limited quantity"
            checked={!!item.productDetails?.includes(LIMITED_QUANTITY)}
            onClick={() => onToggleLimitedQuantity(key)}
          />
          <CheckToggle label="Hazardous material" tone="error" checked={!!item.hazmat} onClick={() => onToggleHazmat(key)} />
        </Box>
        {removable && (
          <Box sx={{ ml: 'auto', pb: '2px' }}>
            <Tooltip title="Remove item">
              <IconButton size="small" aria-label={`Remove ${name}`} onClick={() => onRemoveItem(key)} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      {item.hazmat && item.hazmatInfo && <HazmatFields info={item.hazmatInfo} onChange={changeHazmat} />}
    </Box>
  );
};

export default memo(OrderItemRow);
